import React, { useContext, useEffect, useState, useRef } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import ScratchCard from 'react-scratchcard-v2';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
import ReactPaginate from 'react-paginate';
import Papa from "papaparse";
import { X, Search, Filter, Calendar, Users, Building, Briefcase, CheckCircle, XCircle } from 'lucide-react';

const UserDashboard = () => {
  const { user, roles, permissions, token, loadUserDetails } = useContext(AuthContext);
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [selectedYear, setSelectedYear] = useState("2025");
  const [count, setCount] = useState(0);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsError, setCampaignsError] = useState(null);
  const [campaignsPagination, setCampaignsPagination] = useState({
    current_page: 1,
    last_page: 1,
    next_page_url: null,
    prev_page_url: null,
    per_page: 10,
    total: 0,
  });
  const [nameSortOrder, setNameSortOrder] = useState("asc"); // 'asc' or 'desc'
  const [showConfetti, setShowConfetti] = useState(false);
  const [isScratched, setIsScratched] = useState(() => {
    const stored = localStorage.getItem('birthdayScratchRevealed');
    return stored === 'true';
  });
  const { width, height } = useWindowSize();

  // Search and Filter States
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const [filters, setFilters] = useState({
    department: "",
    status: "",
    role: "",
    dateRange: "",
    manager: ""
  });
  const [filteredData, setFilteredData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [searchSuggestions, setSearchSuggestions] = useState([]);

  const chartRef = useRef(null);
  const donutRef = useRef(null);

  // // console.log(user, roles)

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  // Search and Filter Functions
  const handleSearch = (query) => {
    setSearchQuery(query);
    generateSearchSuggestions(query);
    applyFiltersAndSearch(query, filters);
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
    applyFiltersAndSearch(searchQuery, newFilters);
  };

  const applyFiltersAndSearch = (query, currentFilters) => {
    let filtered = [...allData];

    // Apply search query
    if (query.trim()) {
      filtered = filtered.filter(item => 
        item.name?.toLowerCase().includes(query.toLowerCase()) ||
        item.role?.toLowerCase().includes(query.toLowerCase()) ||
        item.manager?.toLowerCase().includes(query.toLowerCase()) ||
        item.id?.toString().includes(query)
      );
    }

    // Apply filters
    if (currentFilters.department) {
      filtered = filtered.filter(item => item.department === currentFilters.department);
    }
    if (currentFilters.status) {
      filtered = filtered.filter(item => item.status === currentFilters.status);
    }
    if (currentFilters.role) {
      filtered = filtered.filter(item => item.role === currentFilters.role);
    }
    if (currentFilters.manager) {
      filtered = filtered.filter(item => item.manager === currentFilters.manager);
    }

    setFilteredData(filtered);
  };

  const clearFilters = () => {
    setFilters({
      department: "",
      status: "",
      role: "",
      dateRange: "",
      manager: ""
    });
    setSearchQuery("");
    setFilteredData(allData);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchSuggestions([]);
    setShowSearchSuggestions(false);
    applyFiltersAndSearch("", filters);
  };

  // Helper function to generate role-based routes (same as sidebar)
  const getRoleBasedRouteLocal = (route) => {
    if (roles.includes("Admin")) {
      return route;
    } else if (roles.includes("Manager")) {
      return route.replace("/admin", "/manager");
    } else if (roles.includes("HR")) {
      return route.replace("/admin", "/hr");
    } else {
      return route.replace("/admin", "/user");
    }
  };

  // Navigation menu data for search suggestions with role-based routes
  const navigationMenu = [
    // General
    { label: "Overview", path: getRoleBasedRouteLocal("/admin"), icon: "🏠", category: "General" },
    { label: "On-boarding", path: getRoleBasedRouteLocal("/admin/on_boarding_form_response"), icon: "👤", category: "General" },
    
    // Users
    { label: "All Users", path: getRoleBasedRouteLocal("/admin/users"), icon: "👥", category: "Users" },
    { label: "Emergency Contacts", path: getRoleBasedRouteLocal("/admin/emergency-contacts"), icon: "📞", category: "Users" },
    
    // Attendance
    { label: "Attendance Management", path: getRoleBasedRouteLocal("/admin/attendance_management"), icon: "📅", category: "Attendance" },
    
    // Payroll (only for HR role)
    ...(roles.includes("HR") ? [
      { label: "Payroll Dashboard", path: getRoleBasedRouteLocal("/admin/payroll/dashboard"), icon: "💰", category: "Payroll" },
      { label: "Employee Details", path: getRoleBasedRouteLocal("/admin/payroll/employee-details"), icon: "👤", category: "Payroll" },
      { label: "Employment Details", path: getRoleBasedRouteLocal("/admin/payroll/employment-details-management"), icon: "📋", category: "Payroll" },
      { label: "Attendance Leave", path: getRoleBasedRouteLocal("/admin/payroll/attendance-leave"), icon: "📊", category: "Payroll" },
      { label: "Payroll Processing", path: getRoleBasedRouteLocal("/admin/payroll/payroll-processing"), icon: "⚙️", category: "Payroll" },
      { label: "Payslip Generation", path: getRoleBasedRouteLocal("/admin/payroll/payslip-generation"), icon: "📄", category: "Payroll" },
      { label: "Salary Structure", path: getRoleBasedRouteLocal("/admin/payroll/salary-structure"), icon: "🏗️", category: "Payroll" },
      { label: "Salary Disbursement", path: getRoleBasedRouteLocal("/admin/payroll/salary-disbursement"), icon: "💸", category: "Payroll" },
      { label: "Statutory Compliance", path: getRoleBasedRouteLocal("/admin/payroll/statutory-compliance"), icon: "⚖️", category: "Payroll" },
      { label: "Payroll Reports", path: getRoleBasedRouteLocal("/admin/payroll/reports"), icon: "📈", category: "Payroll" },
      { label: "Payroll Settings", path: getRoleBasedRouteLocal("/admin/payroll/settings"), icon: "⚙️", category: "Payroll" },
    ] : []),
    
    // Recruitment (for HR and Manager roles)
    ...(['HR', 'Manager'].some(r => roles.includes(r)) ? [
      { label: "Recruitment Dashboard", path: getRoleBasedRouteLocal("/admin/recruitment-dashboard"), icon: "🎯", category: "Recruitment" },
      { label: "Hiring Requests", path: getRoleBasedRouteLocal("/admin/hiringrequests"), icon: "📝", category: "Recruitment" },
      { label: "Campaigns", path: getRoleBasedRouteLocal("/admin/campaigns"), icon: "📢", category: "Recruitment" },
      { label: "Forms", path: getRoleBasedRouteLocal("/admin/formlist"), icon: "📋", category: "Recruitment" },
      { label: "Form Responses", path: getRoleBasedRouteLocal("/admin/form_response"), icon: "📤", category: "Recruitment" },
      { label: "All Candidates", path: getRoleBasedRouteLocal("/admin/candidates"), icon: "👥", category: "Recruitment" },
      { label: "Interviews", path: getRoleBasedRouteLocal("/admin/interviews"), icon: "🤝", category: "Recruitment" },
      { label: "Scheduled Interviews", path: getRoleBasedRouteLocal("/admin/scheduled-interviews"), icon: "📅", category: "Recruitment" },
      { label: "My Scheduled Interviews", path: getRoleBasedRouteLocal("/admin/my-scheduled-interviews"), icon: "👤", category: "Recruitment" },
    ] : []),
    // HOD Requests (only for HOD role)
    ...(roles.includes('HOD') ? [
      { label: "HOD Requests", path: getRoleBasedRouteLocal("/admin/hod-requests"), icon: "👨‍💼", category: "Recruitment" },
    ] : []),
    
    // File Management (for Admin, Manager, HR roles)
    ...(['Admin'].some(r => roles.includes(r)) ? [
      { label: "Upload Resumes", path: getRoleBasedRouteLocal("/admin/upload-resumes"), icon: "📤", category: "File Management" },
      { label: "DOC Generator", path: getRoleBasedRouteLocal("/admin/doc-generator"), icon: "📄", category: "File Management" },
      { label: "Manage Files", path: getRoleBasedRouteLocal("/admin/manage-files"), icon: "📁", category: "File Management" },
    ] : []),
    
    // Settings (for Admin, Manager, HR roles)
    ...(['Admin'].some(r => roles.includes(r)) ? [
      { label: "Roles", path: getRoleBasedRouteLocal("/admin/roles"), icon: "🔐", category: "Settings" },
      { label: "Permissions", path: getRoleBasedRouteLocal("/admin/permissions"), icon: "🔑", category: "Settings" },
    ] : []),
    
    // Master (for Admin, Manager, HR roles)
    ...(['Admin'].some(r => roles.includes(r)) ? [
      { label: "Employment Types", path: getRoleBasedRouteLocal("/admin/master/employment-type"), icon: "💼", category: "Master" },
      { label: "Departments", path: getRoleBasedRouteLocal("/admin/master/departments"), icon: "🏢", category: "Master" },
      { label: "Designations", path: getRoleBasedRouteLocal("/admin/master/designations"), icon: "🎖️", category: "Master" },
      { label: "Workplace", path: getRoleBasedRouteLocal("/admin/master/workplace"), icon: "🏢", category: "Master" },
      { label: "Leave Types", path: getRoleBasedRouteLocal("/admin/master/leave-type"), icon: "🏖️", category: "Master" },
      { label: "Shifts", path: getRoleBasedRouteLocal("/admin/master/shifts"), icon: "⏰", category: "Master" },
      { label: "Holidays", path: getRoleBasedRouteLocal("/admin/master/holidays"), icon: "🎉", category: "Master" },
      { label: "Leave Balance Masters", path: getRoleBasedRouteLocal("/admin/master/leave-balance-master"), icon: "⚖️", category: "Master" },
      { label: "Forms Masters", path: getRoleBasedRouteLocal("/admin/master/forms-master"), icon: "📋", category: "Master" },
    ] : []),
  ];

  // Generate search suggestions based on navigation menu
  const generateSearchSuggestions = (query) => {
    if (!query.trim()) {
      setSearchSuggestions([]);
      setShowSearchSuggestions(false);
      return;
    }

    const suggestions = navigationMenu.filter(item =>
      item.label.toLowerCase().includes(query.toLowerCase()) ||
      item.category.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 8); // Limit to 8 suggestions

    setSearchSuggestions(suggestions);
    setShowSearchSuggestions(suggestions.length > 0);
  };
  
  useEffect(() => {
    // Retrieve login success message
    const loginMessage = localStorage.getItem("loginMessage");
    if (loginMessage) {
      setNotification(loginMessage);
      localStorage.removeItem("loginMessage"); // Clear after displaying
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('birthdayScratchRevealed', isScratched);
  }, [isScratched]);

  // Fetch campaigns with pagination
  const fetchCampaigns = async (page = 1) => {
    setCampaignsLoading(true);
    setCampaignsError(null);
    try {
      const res = await axios.get(`https://hrmsnew.prepstripe.com/api/campaigns-active?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = res.data.data;
      setActiveCampaigns(data.data || []);
      setCampaignsPagination({
        current_page: data.current_page,
        last_page: data.last_page,
        next_page_url: data.next_page_url,
        prev_page_url: data.prev_page_url,
        per_page: data.per_page,
        total: data.total,
      });
    } catch (err) {
      setCampaignsError("Failed to load active campaigns");
    } finally {
      setCampaignsLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns(1);
  }, [token]);

  // if (!user) {
  //   return <div>Loading user data...</div>;
  // }

    // Sample data - Initialize allData and filteredData
  const [employees] = useState([
    { id: '2663', name: 'Nathan Gustin', role: 'UI/UX designer', status: 'Active', manager: 'Pranav Patil', department: 'Design' },
    { id: '2659', name: 'Sarah Johnson', role: 'Frontend Developer', status: 'Active', manager: 'Pranav Patil', department: 'Engineering' },
    { id: '2664', name: 'Mike Chen', role: 'Backend Developer', status: 'Inactive', manager: 'John Smith', department: 'Engineering' },
    { id: '2653', name: 'Emily Davis', role: 'HR Manager', status: 'Active', manager: 'Lisa Wilson', department: 'Human Resources' },
    { id: '2665', name: 'Alex Brown', role: 'Marketing Specialist', status: 'Active', manager: 'David Lee', department: 'Marketing' },
    { id: '2666', name: 'Jessica White', role: 'Product Manager', status: 'Active', manager: 'Pranav Patil', department: 'Product' },
  ]);

  // Initialize data when component mounts
  useEffect(() => {
    setAllData(employees);
    setFilteredData(employees);
  }, [employees]);

  const [meetings] = useState([
    { title: 'Marketing Meeting', type: 'Meeting', time: '8:00 am' },
    { title: 'Meeting with Designer Samba', type: 'Meeting', time: '9:00 am' },
    { title: 'Development Meeting', type: 'Meeting', time: '9:00 am' },
    { title: 'Marketing Meeting', type: 'Meeting', time: '8:00 am' },
    { title: 'Check in with Clients', type: 'Meeting', time: '12:00 pm' },
    { title: 'Marketing Meeting', type: 'Meeting', time: '8:00 am' },
    { title: 'Check in with Clients', type: 'Meeting', time: '12:00 pm' },
  ]);

  const [searchTerm, setSearchTerm] = useState('');

  const filteredEmployees = employees.filter(employee =>
    employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const CountUpNumber = ({ target }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
      let start = 0;
      const duration = 1000; // total duration in ms
      const stepTime = Math.max(Math.floor(duration / target), 10);

      const interval = setInterval(() => {
        start += 1;
        setCount(start);
        if (start >= target) {
          clearInterval(interval);
        }
      }, stepTime);

      return () => clearInterval(interval);
    }, [target]);

    return <span>{count}</span>;
  };


  const QuickAction = ({ title, icon: Icon, bg_color: bg_color, link: link }) => (
    <Link to={link ? getRoleBasedRouteLocal(link) : "#"} className="flex items-center text-md gap-2 border cursor-pointer font-poppins dark:bg-black border-[#E2E8F0] dark:text-white dark:hover:border-blue-500 dark:border-gray-700 hover:border-blue-500 transform transition duration-300 p-2 rounded-[15px]">
      <div className={`${bg_color} text-white p-1.5 rounded-[11px]`}>
        {Icon}
      </div>
      {title}
    </Link>
  );

  const RecentActivities = ({ title, sub_title, icon: Icon, time_ago, bg_color: bg_color }) => (
    <div className="flex justify-between items-center cursor-pointer text-lg gap-2 transform transition duration-300 p-2 rounded-[15px]">
      <div className="flex items-center gap-2">
        <div className={`${bg_color} text-white p-1.5 rounded-full`}>
          {Icon}
        </div>
        <div className="flex flex-col leading-normal text-[14px]">
          <p className="text-black dark:text-white font-poppins">{title}</p>
          <p className="text-[#6B7280] font-poppins">{sub_title}</p>
        </div>
      </div>  
      <p className="text-[#9CA3AF] text-sm font-poppins">{time_ago}</p>
    </div>
  );

  // Sort campaigns by name for the table
  const sortedCampaigns = [...activeCampaigns].sort((a, b) => {
    if (!a.campaign_name || !b.campaign_name) return 0;
    if (nameSortOrder === "asc") {
      return a.campaign_name.localeCompare(b.campaign_name);
    } else {
      return b.campaign_name.localeCompare(a.campaign_name);
    }
  });

  // Scratch card settings 
  const scratchSettings = {
    width: 300,
    height: 200,
    image: 'https://images.pexels.com/photos/2117937/pexels-photo-2117937.jpeg', // Place a 300x200 gray PNG in public/
    finishPercent: 60,
    onComplete: () => {
      setShowConfetti(true);
      setIsScratched(true);
      setTimeout(() => setShowConfetti(false), 3000);
    },
  };

  // Custom hook to get employee count from CSV
  function useEmployeeCountFromCSV(csvUrl) {
    const [employeeCount, setEmployeeCount] = useState(null);
    const [lastMonthEmployeeCount, setLastMonthEmployeeCount] = useState(null);
    useEffect(() => {
      fetch(csvUrl)
        .then(res => res.text())
        .then(text => {
          Papa.parse(text, {
            header: true,
            complete: (results) => {
              const now = new Date();
              const currentMonth = now.getMonth();
              const currentYear = now.getFullYear();
              // Employees with DOJ up to end of last month
              const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
              const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
              let total = 0;
              let lastMonthTotal = 0;
              results.data.forEach(row => {
                const doj = row["DOJ"];
                if (!doj) return;
                const [day, mon, year] = doj.split('-');
                if (!day || !mon || !year) return;
                let fullYear = parseInt(year, 10);
                fullYear += fullYear < 50 ? 2000 : 1900;
                const monthIndex = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].indexOf(mon);
                if (monthIndex === -1) return;
                const dojDate = new Date(fullYear, monthIndex, parseInt(day, 10));
                if (row["Employee Name"] && row["Employee Name"].trim() !== "") {
                  total += 1;
                  // If DOJ is before the first day of this month, count for last month
                  if (
                    dojDate.getFullYear() < currentYear ||
                    (dojDate.getFullYear() === currentYear && dojDate.getMonth() < currentMonth)
                  ) {
                    lastMonthTotal += 1;
                  }
                }
              });
              setEmployeeCount(total);
              setLastMonthEmployeeCount(lastMonthTotal);
            }
          });
        });
    }, [csvUrl]);
    return [employeeCount, lastMonthEmployeeCount];
  }

  // Custom hook to get new hires count for current month from CSV
  function useNewHiresCountFromCSV(csvUrl) {
    const [newHiresCount, setNewHiresCount] = useState(null);
    const [lastMonthCount, setLastMonthCount] = useState(null);
    useEffect(() => {
      fetch(csvUrl)
        .then(res => res.text())
        .then(text => {
          Papa.parse(text, {
            header: true,
            complete: (results) => {
              const now = new Date();
              const currentMonth = now.getMonth();
              const currentYear = now.getFullYear();
              const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
              const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
              let thisMonth = 0;
              let prevMonth = 0;
              results.data.forEach(row => {
                const doj = row["DOJ"];
                if (!doj) return;
                const [day, mon, year] = doj.split('-');
                if (!day || !mon || !year) return;
                let fullYear = parseInt(year, 10);
                fullYear += fullYear < 50 ? 2000 : 1900;
                const monthIndex = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].indexOf(mon);
                if (monthIndex === -1) return;
                const dojDate = new Date(fullYear, monthIndex, parseInt(day, 10));
                if (dojDate.getMonth() === currentMonth && dojDate.getFullYear() === currentYear) {
                  thisMonth++;
                } else if (dojDate.getMonth() === lastMonth && dojDate.getFullYear() === lastMonthYear) {
                  prevMonth++;
                }
              });
              setNewHiresCount(thisMonth);
              setLastMonthCount(prevMonth);
            }
          });
        });
    }, [csvUrl]);
    return [newHiresCount, lastMonthCount];
  }

  // Custom hook to get department with most employees
  function useTopDepartmentFromCSV(csvUrl) {
    const [topDepartment, setTopDepartment] = useState(null);
    useEffect(() => {
      fetch(csvUrl)
        .then(res => res.text())
        .then(text => {
          Papa.parse(text, {
            header: true,
            complete: (results) => {
              const deptCount = {};
              results.data.forEach(row => {
                const dept = row["Department"];
                const name = row["Employee Name"];
                if (dept && name && name.trim() !== "") {
                  deptCount[dept] = (deptCount[dept] || 0) + 1;
                }
              });
              let maxDept = null;
              let maxCount = 0;
              Object.entries(deptCount).forEach(([dept, count]) => {
                if (count > maxCount) {
                  maxDept = dept;
                  maxCount = count;
                }
              });
              setTopDepartment(maxDept ? `${maxDept} (${maxCount})` : null);
            }
          });
        });
    }, [csvUrl]);
    return topDepartment;
  }

  // Custom hook to get all departments and their counts
  function useAllDepartmentsFromCSV(csvUrl) {
    const [departments, setDepartments] = useState(null);
    useEffect(() => {
      fetch(csvUrl)
        .then(res => res.text())
        .then(text => {
          Papa.parse(text, {
            header: true,
            complete: (results) => {
              const deptCount = {};
              results.data.forEach(row => {
                const dept = row["Department"];
                const name = row["Employee Name"];
                if (dept && name && name.trim() !== "") {
                  deptCount[dept] = (deptCount[dept] || 0) + 1;
                }
              });
              // Convert to array and sort by count descending
              const sorted = Object.entries(deptCount)
                .map(([dept, count]) => ({ dept, count }))
                .sort((a, b) => b.count - a.count);
              setDepartments(sorted);
            }
          });
        });
    }, [csvUrl]);
    return departments;
  }

  // Custom hook to get skilled/unskilled counts
  function useSkilledUnskilledFromCSV(csvUrl) {
    const [data, setData] = useState(null);
    useEffect(() => {
      fetch(csvUrl)
        .then(res => res.text())
        .then(text => {
          Papa.parse(text, {
            header: true,
            complete: (results) => {
              let skilled = 0;
              let unskilled = 0;
              results.data.forEach(row => {
                const val = row["Skilled & Unskilled"];
                const name = row["Employee Name"];
                if (name && name.trim() !== "") {
                  if (val) {
                    const first = val.split(',')[0].trim().toLowerCase();
                    if (first === "skilled") skilled++;
                    else if (first === "unskilled") unskilled++;
                  }
                }
              });
              setData({ skilled, unskilled });
            }
          });
        });
    }, [csvUrl]);
    return data;
  }

  const [employeeCount, lastMonthEmployeeCount] = useEmployeeCountFromCSV('/IBVL Employee Master Data - Omkar.csv');
  const [newHiresCount, lastMonthHiresCount] = useNewHiresCountFromCSV('/IBVL Employee Master Data - Omkar.csv');
  const topDepartment = useTopDepartmentFromCSV('/IBVL Employee Master Data - Omkar.csv');
  const allDepartments = useAllDepartmentsFromCSV('/IBVL Employee Master Data - Omkar.csv');
  const skilledUnskilled = useSkilledUnskilledFromCSV('/IBVL Employee Master Data - Omkar.csv');

  // Color palette for department icons
  const deptColors = [
    '#22C55E', // green
    '#A855F7', // purple
    '#F97316', // orange
    '#EF4444', // red
    '#14B8A6', // teal
    '#2563EB', // blue
    '#EAB308', // yellow
    '#F59E42', // amber
  ];

  return (

    <div className="font-sans min-h-screen">
      {/* max-w-7xl removed */}
      <div className="px-4 py-2  mx-auto">
        {/* Header */}

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12 mt-4">
          {/* Left Section */}
          <div className="flex flex-col">
            <h1 className="text-2xl font-semibold font-poppins text-gray-900 dark:text-white">
              {roles[0]} Dashboard
            </h1>
            <small className="text-[#4B5563] font-poppins text-[16px] dark:text-gray-400">
              Welcome back, <span className="font-semibold">{user?.name}</span>! Here's what's happening at your company.
            </small>
          </div>

          {/* Right Section */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full md:w-auto">
            <div className="relative w-full sm:w-[247px]">
              <Search className="absolute top-[13px] left-3 w-5 h-5 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchQuery && setShowSearchSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSearchSuggestions(false), 200)}
                className="dark:bg-gray-900 dark:text-white border font-poppins placeholder:font-poppins dark:border-gray-700 dark:focus:border-blue-500 dark:focus:border-2 bg-white text-black border-[#DEDEDE] placeholder:text-[#7A7B7F] rounded-[15px] w-full h-[45px] px-10 focus:outline-none"
                placeholder="Search employees, roles, managers, or navigate..."
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute top-[13px] right-3 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}

              {/* Search Suggestions Dropdown */}
              {showSearchSuggestions && searchSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                  <div className="p-2">
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-2">
                      Quick Navigation
                    </div>
                    {searchSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          navigate(suggestion.path);
                          setSearchQuery("");
                          setSearchSuggestions([]);
                          setShowSearchSuggestions(false);
                        }}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors cursor-pointer"
                      >
                        <span className="text-lg">{suggestion.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {suggestion.label}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {suggestion.category}
                          </div>
                        </div>
                        <div className="text-xs text-gray-400 dark:text-gray-500">
                          →
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button 
              onClick={() => setShowFilterModal(true)}
              className="flex items-center justify-center space-x-2 text-[16px] dark:bg-gray-900 dark:border-gray-700 dark:text-white text-black border border-[#DEDEDE] rounded-[15px] px-4 py-2.5 bg-[#FFF9F9] hover:border-gray-500 w-full sm:w-auto"
            >
              <Filter className="w-4 h-4 text-[#313133] dark:text-white" />
              <span className="font-poppins">Filter</span>
              {(filters.department || filters.status || filters.role || filters.manager) && (
                <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {Object.values(filters).filter(Boolean).length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 gap-6">
          {/* Left Column - Stats and Chart */}
          <div className="col-span-4 space-y-6">

            {/* Top Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Card 1 */}
              <div className="bg-white dark:bg-gray-900 rounded-[20px] px-3 py-[11px] shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="text-[15px] font-semibold text-[#565563] dark:text-white mt-1 font-poppins">Total Employees</div>
                    <div className="text-3xl font-semibold font-poppins text-gray-900 dark:text-white">
                      {employeeCount !== null ? (
                        employeeCount
                      ) : (
                        <span className="inline-block align-middle">
                          <span className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full inline-block"></span>
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2 items-center text-sm font-poppins" style={{ color: employeeCount !== null && lastMonthEmployeeCount !== null ? (employeeCount - lastMonthEmployeeCount > 0 ? '#16A34A' : employeeCount - lastMonthEmployeeCount < 0 ? '#DC2626' : '#6B7280') : '#16A367' }}>
                      <svg className={employeeCount !== null && lastMonthEmployeeCount !== null ? (employeeCount - lastMonthEmployeeCount > 0 ? 'text-[#16A34A]' : employeeCount - lastMonthEmployeeCount < 0 ? 'text-[#DC2626]' : 'text-[#6B7280]') : 'text-[#16A34A]'} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M23 6L13.5 15.5L8.5 10.5L1 18" stroke={employeeCount !== null && lastMonthEmployeeCount !== null ? (employeeCount - lastMonthEmployeeCount > 0 ? '#16A34A' : employeeCount - lastMonthEmployeeCount < 0 ? '#DC2626' : '#6B7280') : '#16A34A'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M17 6H23V12" stroke={employeeCount !== null && lastMonthEmployeeCount !== null ? (employeeCount - lastMonthEmployeeCount > 0 ? '#16A34A' : employeeCount - lastMonthEmployeeCount < 0 ? '#DC2626' : '#6B7280') : '#16A34A'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {employeeCount !== null && lastMonthEmployeeCount !== null ? (
                        (employeeCount - lastMonthEmployeeCount > 0 ? '+' : '') + (employeeCount - lastMonthEmployeeCount) + ' from last month'
                      ) : (
                        'This month'
                      )}
                    </div>
                  </div>
                  <div className="w-10 h-10 bg-[#2563EB] text-white rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6" data-slot="icon" aria-hidden="true" fill="none" stroke-width="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Card 2 */}
              <div className="bg-white dark:bg-gray-900 rounded-[20px] px-3 py-[11px] shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="text-[15px] font-semibold text-[#565563] dark:text-white mt-1 font-poppins">New Hires</div>
                    <div className="text-3xl font-semibold font-poppins text-gray-900 dark:text-white">
                      {newHiresCount !== null ? (
                        newHiresCount
                      ) : (
                        <span className="inline-block align-middle">
                          <span className="animate-spin h-6 w-6 border-2 border-green-500 border-t-transparent rounded-full inline-block"></span>
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2 items-center text-sm font-poppins" style={{ color: newHiresCount !== null && lastMonthHiresCount !== null ? (newHiresCount - lastMonthHiresCount > 0 ? '#16A34A' : newHiresCount - lastMonthHiresCount < 0 ? '#DC2626' : '#6B7280') : '#16A367' }}>
                      <svg className={newHiresCount !== null && lastMonthHiresCount !== null ? (newHiresCount - lastMonthHiresCount > 0 ? 'text-[#16A34A]' : newHiresCount - lastMonthHiresCount < 0 ? 'text-[#DC2626]' : 'text-[#6B7280]') : 'text-[#16A34A]'} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M23 6L13.5 15.5L8.5 10.5L1 18" stroke={newHiresCount !== null && lastMonthHiresCount !== null ? (newHiresCount - lastMonthHiresCount > 0 ? '#16A34A' : newHiresCount - lastMonthHiresCount < 0 ? '#DC2626' : '#6B7280') : '#16A34A'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M17 6H23V12" stroke={newHiresCount !== null && lastMonthHiresCount !== null ? (newHiresCount - lastMonthHiresCount > 0 ? '#16A34A' : newHiresCount - lastMonthHiresCount < 0 ? '#DC2626' : '#6B7280') : '#16A34A'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {newHiresCount !== null && lastMonthHiresCount !== null ? (
                        (newHiresCount - lastMonthHiresCount > 0 ? '+' : '') + (newHiresCount - lastMonthHiresCount) + ' from last month'
                      ) : (
                        'This month'
                      )}
                    </div>
                  </div>
                  <div className="w-10 h-10 bg-[#22C55E] text-white rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6" data-slot="icon" aria-hidden="true" fill="none" stroke-width="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Card 3 */}
              <div className="bg-white dark:bg-gray-900 rounded-[20px] px-3 py-[11px] shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="text-[15px] font-semibold text-[#565563] dark:text-white mt-1 font-poppins">Top Department</div>
                    {topDepartment !== null ? (
                      <>
                        <div className="text-2xl font-semibold font-poppins text-gray-900 dark:text-white">
                          {topDepartment.split(' (')[0]}
                        </div>
                        <div className="text-sm font-poppins text-gray-500 dark:text-gray-300">
                          {topDepartment.match(/\((\d+)\)/) ? topDepartment.match(/\((\d+)\)/)[1] + ' Employees' : ''}
                        </div>
                      </>
                    ) : (
                      <span className="inline-block align-middle">
                        <span className="animate-spin h-6 w-6 border-2 border-purple-500 border-t-transparent rounded-full inline-block"></span>
                      </span>
                    )}
                    {/* <div className="flex gap-2 items-center text-sm font-poppins text-[#7A7B7F]">
                      Department with most employees
                    </div> */}
                  </div>
                  <div className="w-10 h-10 bg-[#E63E3E] text-white rounded-lg flex items-center justify-center">
                    {/* Department/Building Icon */}
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 21V5.25A2.25 2.25 0 0 1 5.25 3h13.5A2.25 2.25 0 0 1 21 5.25V21M3 21h18M3 21v-2.25A2.25 2.25 0 0 1 5.25 16.5h13.5A2.25 2.25 0 0 1 21 18.75V21M9 21v-4.5a1.5 1.5 0 0 1 3 0V21" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Card 4 */}
              <div className="bg-white dark:bg-gray-900 rounded-[20px] px-3 py-[11px] shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="text-[15px] font-semibold text-[#565563] dark:text-white mt-1 font-poppins">Skilled / Unskilled</div>
                    <div className="text-3xl font-semibold font-poppins text-gray-900 dark:text-white">
                      {skilledUnskilled === null ? (
                        <span className="inline-block align-middle">
                          <span className="animate-spin h-6 w-6 border-2 border-purple-500 border-t-transparent rounded-full inline-block"></span>
                        </span>
                      ) : (
                        `${skilledUnskilled.skilled} / ${skilledUnskilled.unskilled}`
                      )}
                    </div>
                    <div className="flex gap-2 items-center text-sm font-poppins text-[#6366F1]">
                      {skilledUnskilled === null ? (
                        'Loading...'
                      ) : (
                        (skilledUnskilled.skilled + skilledUnskilled.unskilled > 0
                          ? `${Math.round((skilledUnskilled.skilled / (skilledUnskilled.skilled + skilledUnskilled.unskilled)) * 100)}% Skilled / ${Math.round((skilledUnskilled.unskilled / (skilledUnskilled.skilled + skilledUnskilled.unskilled)) * 100)}% Unskilled`
                          : '--')
                      )}
                    </div>
                  </div>
                  <div className="w-10 h-10 bg-[#A855F7] text-white rounded-lg flex items-center justify-center">
                    {/* User Group/Team Icon */}
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" strokeLinecap="round" strokeLinejoin="round" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M23 20v-2a4 4 0 0 0-3-3.87" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Right Column */}
          <div className="col-span-4 space-y-6">

          </div>
        </div>

        {/* quick action and recent activities */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">

          <div className="col-span-1 lg:col-span-2 row-span-2">
            <div className="flex flex-col gap-y-6 bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-[20px] px-4 py-4">
              <h1 className="text-[20px] dark:text-white font-medium font-poppins">
                Quick Actions
              </h1>
              <div className="flex flex-col gap-y-3">
                {/* Add Employee - for Admin, Manager, HR */}
                {['Admin', 'Manager', 'HR'].some(r => roles.includes(r)) && (
                  <QuickAction link={"/admin/users"} bg_color={"bg-[#3B82F6]"} title={"Add Employee"} icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" class="main-grid-item-icon" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="8.5" cy="7" r="4" />
                        <line x1="20" x2="20" y1="8" y2="14" />
                        <line x1="23" x2="17" y1="11" y2="11" />
                      </svg>
                    }
                  />
                )}

                {/* Mark Attendance - for all roles */}
                <QuickAction link={"/admin/attendance_management"} bg_color={"bg-[#22C55E]"} title={"Mark Attendance"} icon={<svg className="w-6 h-6" data-slot="icon" aria-hidden="true" fill="none" stroke-width="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>
                    }
                />

                {/* Process Payroll - for HR only */}
                {roles.includes("HR") && (
                  <QuickAction link={"/admin/payroll/payroll-processing"} bg_color={"bg-[#A855F7]"} title={"Process Payroll"} icon={<svg className="w-6 h-6" data-slot="icon" aria-hidden="true" fill="none" stroke-width="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 8.25H9m6 3H9m3 6-3-3h1.5a3 3 0 1 0 0-6M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" stroke-linecap="round" stroke-linejoin="round"></path>
                      </svg>
                    }
                  />
                )}

                {/* Schedule Shift - for Admin, Manager, HR */}
                {['Admin', 'Manager', 'HR'].some(r => roles.includes(r)) && (
                  <QuickAction link={"/admin/master/shifts"} bg_color={"bg-[#F97316]"} title={"Schedule Shift"} icon={<svg className="w-6 h-6" data-slot="icon" aria-hidden="true" fill="none" stroke-width="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" stroke-linecap="round" stroke-linejoin="round"></path>
                      </svg>
                    }
                  />
                )}

                {/* Generate Report - for Admin, Manager, HR */}
                {['Admin', 'Manager', 'HR'].some(r => roles.includes(r)) && (
                  <QuickAction link={"/admin/payroll/reports"} bg_color={"bg-[#EF4444]"} title={"Generate Report"} icon={<svg className="w-6 h-6" data-slot="icon" aria-hidden="true" fill="none" stroke-width="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" stroke-linecap="round" stroke-linejoin="round"></path>
                      </svg>
                    }
                  />
                )}

                {/* View All Employees - for Admin, Manager, HR */}
                {['Admin', 'Manager', 'HR'].some(r => roles.includes(r)) && (
                  <QuickAction link={"/admin/users"} bg_color={"bg-[#14B8A6]"} title={"View All Employees"} icon={<svg className="w-6 h-6" data-slot="icon" aria-hidden="true" fill="none" stroke-width="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" stroke-linecap="round" stroke-linejoin="round"></path>
                      </svg>
                    }
                  />
                )}

                {/* Employee Types - for Admin, Manager, HR */}
                {['Admin', 'Manager', 'HR'].some(r => roles.includes(r)) && (
                  <QuickAction link={"/admin/master/employment-type"} bg_color={"bg-[#6B7280]"} title={"Employee Types"} icon={<svg className="w-6 h-6" data-slot="icon" aria-hidden="true" fill="none" stroke-width="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" stroke-linecap="round" stroke-linejoin="round"></path>
                      </svg>
                    }
                  />
                )}

                {/* Recruitment Dashboard - for HR and Manager */}
                {['HR', 'Manager'].some(r => roles.includes(r)) && (
                  <QuickAction link={"/admin/recruitment-dashboard"} bg_color={"bg-[#8B5CF6]"} title={"Recruitment"} icon={<svg className="w-6 h-6" data-slot="icon" aria-hidden="true" fill="none" stroke-width="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" stroke-linecap="round" stroke-linejoin="round"></path>
                      </svg>
                    }
                  />
                )}

                {/* Emergency Contacts - for Admin and HR */}
                {['Admin', 'HR'].some(r => roles.includes(r)) && (
                  <QuickAction link={"/admin/emergency-contacts"} bg_color={"bg-[#F59E0B]"} title={"Emergency Contacts"} icon={<svg className="w-6 h-6" data-slot="icon" aria-hidden="true" fill="none" stroke-width="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" stroke-linecap="round" stroke-linejoin="round"></path>
                      </svg>
                    }
                  />
                )}
              </div>
            </div>
          </div>
          
          <div className="col-span-1 lg:col-span-3">
            <div className="flex flex-col gap-y-4 bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-[20px] px-4 py-4">
              <div className="flex justify-between items-center">
                <p className="text-[20px] font-medium font-poppins dark:text-white">Recent Activity</p>
                <button className="border px-4 py-1.5 font-poppins rounded-[15px] transition transform duration-300 hover:border-blue-500 dark:text-white dark:border-gray-700 dark:hover:border-blue-500">View All</button>
              </div>
              <div className="flex flex-col gap-y-5">
                  <RecentActivities bg_color={"bg-[#22C55E]"} title={"New employee onboarded"} sub_title={"Sarah Johnson joined as Software Engineer"} time_ago={"1 hours ago"} icon={<svg className="w-6 h-6" data-slot="icon" aria-hidden="true" fill="none" stroke-width="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>}
                  />

                  <RecentActivities bg_color={"bg-[#3B82F6]"} title={"Payroll processed"} sub_title={"Monthly payroll for Engineering team completed"} time_ago={"2 hours ago"} icon={<svg className="w-6 h-6" data-slot="icon" aria-hidden="true" fill="none" stroke-width="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 8.25H9m6 3H9m3 6-3-3h1.5a3 3 0 1 0 0-6M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>}
                  />

                  <RecentActivities bg_color={"bg-[#F97316]"} title={"Shift schedule updated"} sub_title={"Night shift assignments for next week"} time_ago={"3 hours ago"} icon={<svg className="w-6 h-6" data-slot="icon" aria-hidden="true" fill="none" stroke-width="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" stroke-linecap="round" stroke-linejoin="round"></path>
                  </svg>}
                  />

                  <RecentActivities bg_color={"bg-[#6B7280]"} title={"Attendance report generated"} sub_title={"Weekly attendance summary ready for review"} time_ago={"5 hours ago"} icon={
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-6 h-6 main-grid-item-icon" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" x2="8" y1="13" y2="13" />
                    <line x1="16" x2="8" y1="17" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                }
                  />

                  <RecentActivities bg_color={"bg-[#22C55E]"} title={"Leave request approved"} sub_title={"Michael's vacation request for next month"} time_ago={"7 hours ago"} icon={
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-6 h-6 main-grid-item-icon" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    }
                  />

                  <RecentActivities bg_color={"bg-[#22D20F]"} title={"Leave request approved"} sub_title={"Michael's vacation request for next month"} time_ago={"8 hours ago"} icon={<svg className="w-6 h-6" data-slot="icon" aria-hidden="true" fill="none" stroke-width="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>}
                  />
                  
              </div>
            </div>
          </div>
        </div>

        {/* department overview */}
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 mt-5">
          <div className="">
           <div className="bg-white dark:bg-gray-900 border dark:border-gray-700 px-4 py-4 rounded-[15px]">
              <div>
                <h1 className="text-black dark:text-white text-lg font-poppins font-medium">Department Overview</h1>
                <p className="text-[#64748B] font-poppins text-sm">Employee distribution across departments</p>
              </div>
              {/* Department Cards Horizontal Slider */}
              <div className="mt-4 overflow-x-auto scrollbar-hide">
                <div className="flex gap-3 min-w-max">
                  {allDepartments === null ? (
                    <div className="flex justify-center items-center h-32 w-full">
                      <span className="inline-block align-middle">
                        <span className="animate-spin h-8 w-8 border-2 border-purple-500 border-t-transparent rounded-full inline-block"></span>
                      </span>
                    </div>
                  ) : (
                    allDepartments.map(({ dept, count }, idx) => (
                      <div key={dept} className="flex flex-col items-center gap-y-3 cursor-pointer dark:bg-black border dark:border-gray-700 dark:hover:border-blue-700 hover:border-blue-500 transform transition duration-300 p-4 rounded-[15px] min-w-[180px]">
                        <div className="p-2 rounded-full" style={{ backgroundColor: deptColors[idx % deptColors.length], color: 'white' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" className="main-grid-item-icon" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                          </svg>
                        </div>
                        <div className="flex flex-col items-center">
                          <p className="dark:text-white font-poppins">{dept}</p>
                          <p className="font-semibold font-poppins dark:text-white">{count}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
           </div>
          </div>
        </div>


        {/* Active Campaigns Section */}
        <div className="grid grid-cols-1 gap-6 mt-5">
          <div className="bg-white dark:bg-gray-900 border dark:border-gray-700 px-4 py-4 rounded-[15px]">
            <h1 className="text-black dark:text-white text-lg font-poppins font-medium mb-2">
              <span className="relative inline-flex items-center">
                <span className="flex h-3 w-3 mr-2">
                  <span className="animate-pulse absolute inline-flex h-full w-full rounded-full opacity-75"></span>
                  <span className="animate-pulse relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
                Active Campaigns
              </span>
            </h1>
            {campaignsLoading ? (
              <div className="text-gray-600 dark:text-gray-300">Loading campaigns...</div>
            ) : campaignsError ? (
              <div className="text-red-500">{campaignsError}</div>
            ) : activeCampaigns.length === 0 ? (
              <div className="text-gray-500">No active campaigns found.</div>
            ) : (
              <>
                {/* Sort campaigns by name */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => setNameSortOrder(order => order === "asc" ? "desc" : "asc")}>Name
                          <span className="ml-1 align-middle">
                            {nameSortOrder === "asc" ? (
                              <svg className="inline w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
                            ) : (
                              <svg className="inline w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                            )}
                          </span>
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From Date</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To Date</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {sortedCampaigns.map((campaign) => (
                        <tr key={campaign.id}>
                          <td className="px-4 py-2 font-poppins text-gray-900 dark:text-white">
                            
                            {campaign.campaign_name}
                            
                          </td>
                          <td className="px-4 py-2 font-poppins text-gray-700 dark:text-gray-200">{campaign.position}</td>
                          <td className="px-4 py-2 font-poppins text-gray-700 dark:text-gray-200">{campaign.from_date}</td>
                          <td className="px-4 py-2 font-poppins text-gray-700 dark:text-gray-200">{campaign.to_date}</td>
                          <td className="px-4 py-2 font-poppins">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${campaign.status === "Active" ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-800"}`}>
                              {campaign.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination Controls */}
                <ReactPaginate
                  breakLabel="..."
                  onPageChange={(event) => fetchCampaigns(event.selected + 1)}
                  forcePage={campaignsPagination.current_page - 1}
                  pageRangeDisplayed={5}
                  pageCount={campaignsPagination.last_page}
                  renderOnZeroPageCount={null}
                  nextLabel="Next >"
                  previousLabel="< Prev"
                  previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
                  nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
                  containerClassName="flex items-center justify-center mt-4"
                  pageClassName="mx-1 px-3 py-1 border rounded dark:text-white"
                  activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700"
                />
              </>
            )}
          </div>
        </div>


      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filter Options</h3>
              <button
                onClick={() => setShowFilterModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Department Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Building className="inline w-4 h-4 mr-2" />
                  Department
                </label>
                <select
                  value={filters.department}
                  onChange={(e) => handleFilterChange('department', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Departments</option>
                  <option value="Design">Design</option>
                  <option value="Engineering">Engineering</option>
                  <option value="Human Resources">Human Resources</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Product">Product</option>
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="inline w-4 h-4 mr-2" />
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              {/* Role Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Briefcase className="inline w-4 h-4 mr-2" />
                  Role
                </label>
                <select
                  value={filters.role}
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Roles</option>
                  <option value="UI/UX designer">UI/UX Designer</option>
                  <option value="Frontend Developer">Frontend Developer</option>
                  <option value="Backend Developer">Backend Developer</option>
                  <option value="HR Manager">HR Manager</option>
                  <option value="Marketing Specialist">Marketing Specialist</option>
                  <option value="Product Manager">Product Manager</option>
                </select>
              </div>

              {/* Manager Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Users className="inline w-4 h-4 mr-2" />
                  Manager
                </label>
                <select
                  value={filters.manager}
                  onChange={(e) => handleFilterChange('manager', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Managers</option>
                  <option value="Pranav Patil">Pranav Patil</option>
                  <option value="John Smith">John Smith</option>
                  <option value="Lisa Wilson">Lisa Wilson</option>
                  <option value="David Lee">David Lee</option>
                </select>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={clearFilters}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
};

export default UserDashboard;


